import React, { useEffect, useMemo, useCallback, useState } from 'react';
import LayoutComponent from '../BaseScreen';
import ModernTableComponent from '../../Components/Tables/ModernTableComponent';
import ClaimDetailPanel from '../../Components/Modals/ClaimDetailPanel';
import { useClaims } from '../../Context/ClaimsContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Calendar, RefreshCw } from 'react-feather';

const ClaimsScreen = () => {
  const {
    claimsRecords,
    claimsLoading,
    grabClaims,
    grabRefreshClaims,
    startDate,
    endDate,
    handleStartDate,
    handleEndDate,
  } = useClaims();

  const [selectedClaim, setSelectedClaim] = useState<any>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  useEffect(() => {
    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
    handleStartDate(sixtyDaysAgo);
    handleEndDate(new Date());
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      grabRefreshClaims();
    }
  }, [startDate, endDate]);

  const records = claimsRecords || [];

  const stats = useMemo(() => {
    const total = records.length;
    const paid = records.filter((r: any) => r.status === 'PAID').length;
    const pending = records.filter((r: any) => !r.status).length;
    const balanceDue = records.filter((r: any) => r.status === 'BALANCE DUE PATIENT').length;
    const atInsurance = records.filter((r: any) => r.status === 'CLAIM AT INSURANCE').length;
    const writeOff = records.filter((r: any) => r.status === 'WRITE OFF- INSURANCE').length;
    return { total, paid, pending, balanceDue, atInsurance, writeOff };
  }, [records]);

  const tabs = [
    { label: 'All Claims', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Paid', value: 'paid' },
    { label: 'Balance Due', value: 'balance_due' },
    { label: 'At Insurance', value: 'at_insurance' },
    { label: 'Write Off', value: 'write_off' },
  ];

  const getTabCount = useCallback((tabValue: string, data: any[]) => {
    switch (tabValue) {
      case 'all': return data.length;
      case 'pending': return data.filter((r: any) => !r.status).length;
      case 'paid': return data.filter((r: any) => r.status === 'PAID').length;
      case 'balance_due': return data.filter((r: any) => r.status === 'BALANCE DUE PATIENT').length;
      case 'at_insurance': return data.filter((r: any) => r.status === 'CLAIM AT INSURANCE').length;
      case 'write_off': return data.filter((r: any) => r.status === 'WRITE OFF- INSURANCE').length;
      default: return 0;
    }
  }, []);

  const columns = [
    { key: 'name', label: 'Patient', type: 'text' as const, width: '180px', primaryOnMobile: true },
    { key: 'cpt_code', label: 'CPT', type: 'text' as const, width: '80px', hideOnMobile: true },
    { key: 'insurance', label: 'Insurance', type: 'text' as const, width: '180px' },
    { key: 'network', label: 'Network', type: 'badge' as const, badgeType: 'network' as const, width: '120px' },
    { key: 'facility', label: 'Facility', type: 'badge' as const, badgeType: 'facility' as const, width: '160px', hideOnMobile: true },
    { key: 'start_date_display', label: 'Start Date', type: 'text' as const, width: '120px', hideOnMobile: true },
    { key: 'end_date_display', label: 'End Date', type: 'text' as const, width: '120px', hideOnMobile: true },
    { key: 'display_status', label: 'Status', type: 'badge' as const, badgeType: 'claim_status' as const, width: '140px' },
  ];

  const formatFacility = (facility: string): string => {
    if (!facility) return '-';
    const f = facility.toLowerCase();
    if (f.includes('beachside')) return 'Beachside';
    if (f.includes('axis')) return 'Axis';
    if (f.includes('affinity')) return 'Affinity';
    if (f.includes('no facility')) return '-';
    return facility;
  };

  const formatDateDisplay = (dateStr: string): string => {
    if (!dateStr) return '-';
    try {
      const match = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (match) {
        const [, mm, dd, yyyy] = match;
        return `${mm}/${dd}/${yyyy}`;
      }
      const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const [, yyyy, mm, dd] = isoMatch;
        return `${mm}/${dd}/${yyyy}`;
      }
      return dateStr;
    } catch { return dateStr; }
  };

  // Convert display date "MM/DD/YYYY" or backend "MM-DD-YYYY" back to "YYYY-MM-DD" for API calls
  const toBackendDate = (dateStr: string): string => {
    if (!dateStr) return '';
    // MM-DD-YYYY format from backend
    const dashMatch = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dashMatch) {
      const [, mm, dd, yyyy] = dashMatch;
      return `${yyyy}-${mm}-${dd}`;
    }
    // MM/DD/YYYY format from display
    const slashMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashMatch) {
      const [, mm, dd, yyyy] = slashMatch;
      return `${yyyy}-${mm}-${dd}`;
    }
    return dateStr;
  };

  const tableData = useMemo(() => {
    return records.map((r: any) => ({
      ...r,
      name_raw: r.name,
      facility_raw: r.facility,
      facility: formatFacility(r.facility),
      display_status: r.status || '',
      charged_display: `$${parseFloat(r.charged_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      paid_display: `$${parseFloat(r.paid_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      balance_display: `$${parseFloat(r.balance_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      start_date_display: formatDateDisplay(r.start_date),
      end_date_display: formatDateDisplay(r.end_date),
    }));
  }, [records]);

  const tabFilterFn = useCallback((row: any, tabValue: string): boolean => {
    const s = row.status || '';
    switch (tabValue) {
      case 'all': return true;
      case 'pending': return !s;
      case 'paid': return s === 'PAID';
      case 'balance_due': return s === 'BALANCE DUE PATIENT';
      case 'at_insurance': return s === 'CLAIM AT INSURANCE';
      case 'write_off': return s === 'WRITE OFF- INSURANCE';
      default: return true;
    }
  }, []);

  const handleRefresh = useCallback(() => {
    grabRefreshClaims();
  }, [grabRefreshClaims]);

  const handleRowClick = useCallback((row: any) => {
    setSelectedClaim(row);
    setIsPanelOpen(true);
  }, []);

  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false);
    setSelectedClaim(null);
  }, []);

  return (
    <LayoutComponent
      header={null}
      content={
        <div className="w-full h-full px-2 md:px-4 py-2 md:py-4 relative overflow-auto">
          {/* Date Range Bar */}
          <div className="flex flex-wrap items-center gap-3 mb-3 px-1">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-400">From</span>
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => date && handleStartDate(date)}
                className="w-[120px] px-3 py-1.5 bg-dark-input border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-accent-teal"
                dateFormat="MM/dd/yyyy"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">To</span>
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => date && handleEndDate(date)}
                className="w-[120px] px-3 py-1.5 bg-dark-input border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-accent-teal"
                dateFormat="MM/dd/yyyy"
              />
            </div>
            <button
              onClick={handleRefresh}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-accent-teal to-accent-purple text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
            <div className="hidden md:flex items-center gap-3 ml-auto text-xs text-gray-500">
              <span>{stats.total.toLocaleString()} total</span>
              <span className="text-green-400">{stats.paid.toLocaleString()} paid</span>
              <span className="text-yellow-400">{stats.pending.toLocaleString()} pending</span>
              <span className="text-orange-400">{stats.balanceDue.toLocaleString()} balance due</span>
              <span className="text-blue-400">{stats.atInsurance.toLocaleString()} at insurance</span>
            </div>
          </div>

          {/* Claims Table */}
          <ModernTableComponent
            tabs={tabs}
            columns={columns}
            data={tableData}
            searchPlaceholder="Search claims by name, ID, insurance..."
            tabFilterFn={tabFilterFn}
            getTabCount={getTabCount}
            pageSize={100}
            defaultSort={{ key: 'end_date_display', direction: 'desc' }}
            onRowClick={handleRowClick}
            selectedRow={selectedClaim}
            isLoading={claimsLoading || claimsRecords === null}
            skeletonRows={12}
          />

          {/* Claim Detail Side Panel */}
          <ClaimDetailPanel
            isOpen={isPanelOpen}
            onClose={handlePanelClose}
            claim={selectedClaim}
          />
        </div>
      }
    />
  );
};

export default ClaimsScreen;
